/**
 * Lightweight performance tracing for Firestore reads.
 * Enabled only when REACT_APP_DEBUG_PERF is set (e.g. "true").
 * Safe to leave in place; strip by removing the env var.
 */
const ENABLED = process.env.REACT_APP_DEBUG_PERF === 'true';

export function perfEnabled() {
  return ENABLED;
}

/** Logs the duration and document count of a one-shot read (get). */
export async function traceGet<T>(
  label: string,
  run: () => Promise<T[]>,
): Promise<T[]> {
  if (!ENABLED) return run();

  const start = performance.now();
  try {
    const docs = await run();
    const ms = Math.round(performance.now() - start);
    console.log(
      `%c[perf] get ${label} → ${docs.length} docs in ${ms}ms`,
      'color:#0a0',
    );
    return docs;
  } catch (error) {
    const ms = Math.round(performance.now() - start);
    console.log(`%c[perf] get ${label} → ERROR in ${ms}ms`, 'color:#c00', error);
    throw error;
  }
}

/**
 * Tracks a realtime listener: time-to-first-snapshot and per-snapshot doc count.
 * Returns a callback to invoke inside onSnapshot's success handler.
 *
 * `fromCache` matters for reading these numbers: with a persisted cache a
 * snapshot can be served locally, so its doc count is not a billed read.
 */
export function traceListen(label: string) {
  if (!ENABLED) return () => undefined;

  const subscribedAt = performance.now();
  let snapshotCount = 0;

  return (docCount: number, fromCache?: boolean) => {
    snapshotCount += 1;
    const ms = Math.round(performance.now() - subscribedAt);
    const kind = snapshotCount === 1 ? 'first snapshot' : `snapshot #${snapshotCount}`;
    const source = fromCache ? 'cache' : 'server';
    console.log(
      `%c[perf] listen ${label} → ${docCount} docs from ${source}, ${kind} at +${ms}ms`,
      'color:#06c',
    );
  };
}

/** Generic timer for a synchronous/async block (e.g. client-side aggregation). */
export async function traceBlock<T>(
  label: string,
  run: () => T | Promise<T>,
): Promise<T> {
  if (!ENABLED) return run();

  const start = performance.now();
  const result = await run();
  const ms = Math.round(performance.now() - start);
  console.log(`%c[perf] block ${label} → ${ms}ms`, 'color:#a60');
  return result;
}
